import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {TABLE_NAME_BY_COMPONENT_NAME} from 'procedures/planning/ProcedureExperimentConstants';
import {
  ExperimentalInputs,
  ExperimentalInputsComponentName,
  ExperimentalInputsPerMethod,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  getVaryingParameterIndices,
  getVisibleConditionIndicesForMethod,
} from 'procedures/planning/useInputTable';
import {getProcedureRunDesignFlowchartNodeId} from 'studies/typed/shared/flowcharts/design/ProcedureRunDesignFlowchartNodeIdUtils';
import {getConditionGroupGridRows} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {
  BaseCondition,
  isConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

export interface VaryingInputRow {
  label: string;
  value: string;
}

export interface VaryingInputSection {
  title: string;
  rows: Array<VaryingInputRow>;
}

export type VaryingInputIndicesByComponent = Record<ExperimentalInputsComponentName, Array<number>>;

const EMPTY_REMOVED_NODE_IDS: ReadonlySet<string> = new Set();

export const EXPERIMENTAL_INPUT_COMPONENT_NAMES_IN_NODE_DISPLAY_ORDER: Array<ExperimentalInputsComponentName> =
  [
    ExperimentalInputsComponentName.PARAMETERS,
    ExperimentalInputsComponentName.MATERIAL_INPUTS,
    ExperimentalInputsComponentName.EQUIPMENT,
  ];

export function getNumFactorsSetForMethod(
  experimentalInputsByMethod: ExperimentalInputs,
  conditions: ReadonlyArray<BaseCondition>,
  methodId: string,
  removedNodeIds: ReadonlySet<string> = EMPTY_REMOVED_NODE_IDS
): number {
  const experimentalInputs = experimentalInputsByMethod[methodId];
  if (experimentalInputs == null) {
    return 0;
  }
  // Intentionally passing showArchivedConditions: false. The factor count should only include active nodes,
  // regardless of whether the "show archived" toggle is enabled. Conditions whose only rendered cell for this
  // method is staged for removal are excluded so the count matches the varying-input cell display.
  const visibleConditionIndices = getRenderedConditionIndicesForMethod(
    conditions,
    methodId,
    false,
    removedNodeIds
  );
  return Object.values(ExperimentalInputsComponentName).reduce(
    (count, componentName) =>
      count + getVaryingParameterIndices(experimentalInputs, componentName, visibleConditionIndices).length,
    0
  );
}

/*
 * For each method, determines which inputs vary across all rendered conditions.
 */
export function getVaryingInputIndicesByMethodId({
  experimentalInputs,
  conditions,
  methods,
  showArchived,
  removedNodeIds = EMPTY_REMOVED_NODE_IDS,
}: {
  experimentalInputs?: ExperimentalInputs;
  conditions: ReadonlyArray<BaseCondition>;
  methods: ReadonlyArray<ExperimentMethod>;
  showArchived: boolean;
  removedNodeIds?: ReadonlySet<string>;
}): Record<string, VaryingInputIndicesByComponent> {
  if (experimentalInputs == null) {
    return {};
  }

  const varyingIndicesByMethodId: Record<string, VaryingInputIndicesByComponent> = {};
  methods.forEach((method) => {
    const inputs = experimentalInputs[method.id];
    if (inputs == null) {
      return;
    }
    const visibleConditionIndices = getRenderedConditionIndicesForMethod(
      conditions,
      method.id,
      showArchived,
      removedNodeIds
    );
    varyingIndicesByMethodId[method.id] = Object.fromEntries(
      Object.values(ExperimentalInputsComponentName).map((componentName) => [
        componentName,
        getVaryingParameterIndices(inputs, componentName, visibleConditionIndices),
      ])
    ) as Record<ExperimentalInputsComponentName, Array<number>>;
  });
  return varyingIndicesByMethodId;
}

/**
 * Builds the display sections for one node, using precomputed varying indices and that condition’s values
 */
export function getVaryingInputSectionsForCell({
  experimentalInputs,
  conditionIndex,
  varyingIndicesByComponent,
}: {
  experimentalInputs: ExperimentalInputsPerMethod;
  conditionIndex: number;
  varyingIndicesByComponent: VaryingInputIndicesByComponent;
}): Array<VaryingInputSection> {
  return EXPERIMENTAL_INPUT_COMPONENT_NAMES_IN_NODE_DISPLAY_ORDER.flatMap((componentName) => {
    const varyingParameterIndices = varyingIndicesByComponent[componentName];
    const rows = varyingParameterIndices.flatMap((parameterIndex): Array<VaryingInputRow> => {
      const parameterName = experimentalInputs.parameterNames[componentName][parameterIndex];
      const fields = experimentalInputs.fields[componentName][parameterIndex];
      const conditionValues = experimentalInputs.values[conditionIndex]?.[componentName][parameterIndex];
      if (parameterName == null || fields == null || conditionValues == null) {
        return [];
      }

      return fields.map((field, fieldIndex) => ({
        label: getVaryingInputRowLabel(parameterName, fields.length, field.displayName),
        value: formatVaryingInputValue(conditionValues[fieldIndex], field.schemaField.unitSymbol),
      }));
    });

    return rows.length > 0 ? [{title: TABLE_NAME_BY_COMPONENT_NAME[componentName], rows}] : [];
  });
}

/**
 * Returns the condition indices currently rendered for a method in the flowchart.
 *
 * Use this for UI projections that must reflect in-session node removals, such as
 * factor counts and the right-side experimental-input table. In addition to normal
 * method coverage and archive visibility, it excludes a condition when archived
 * conditions are hidden and every cell for this method is staged for removal.
 */
export function getRenderedConditionIndicesForMethod(
  conditions: ReadonlyArray<BaseCondition>,
  methodId: string,
  showArchived: boolean,
  removedNodeIds: ReadonlySet<string> = EMPTY_REMOVED_NODE_IDS
): Array<number> {
  return getVisibleConditionIndicesForMethod(conditions, methodId, showArchived).filter((conditionIndex) => {
    const condition = conditions[conditionIndex];
    if (condition == null) {
      return false;
    }
    if (!isConditionGroup(condition)) {
      return condition.numReplicates > 0;
    }
    return getConditionGroupGridRows(condition, [{id: methodId}], showArchived).some((row) =>
      row.cells.some(
        (cell) =>
          cell.methodId === methodId &&
          (showArchived ||
            !removedNodeIds.has(
              getProcedureRunDesignFlowchartNodeId({
                conditionIndex,
                replicateIndex: row.replicateNumber - 1,
                methodId,
              })
            ))
      )
    );
  });
}

/*
 * Provides row label for a single field in a varying input section.
 *
 * Appends the field display name to the parameter name to disambiguate when there are multiple fields.
 */
function getVaryingInputRowLabel(parameterName: string, numFields: number, fieldDisplayName: string): string {
  return numFields > 1 ? `${parameterName} ${fieldDisplayName.toLowerCase()}` : parameterName;
}

/**
 * Converts a typed value into display text, appending the unit when present.
 */
export function formatVaryingInputValue(
  typedValue: TypedValue | null | undefined,
  unitSymbol: string | null | undefined
): string {
  if (typedValue == null || isTypedValueEmpty(typedValue)) {
    return '';
  }
  const value = formatTypedValue(typedValue);
  if (!value) {
    return '';
  }
  return unitSymbol ? `${value} ${unitSymbol}` : value;
}
